import { defineStore } from 'pinia';

/**
 * Gamification onboarding tour visibility + "seen" state (M4C-27).
 *
 * Holds the shared open/closed state for the tour so the trigger can auto-open it
 * and (once M4C-20 ships) the "How it works" links can reopen it on demand.
 *
 * Persistence is intentionally **per auth session** for now (dev/QA): the "seen"
 * flag lives in `sessionStorage` and is cleared on logout, so the tour re-appears
 * on every fresh login for inspection. Production intent (M4C-27) is once-per-user
 * — swap `markSeen`/`hasSeenThisSession` for a real `profile.tour_seen` API flag
 * when the backend lands (the gamification service is M4C-25).
 */
const SEEN_KEY = 'gami_tour_seen';

export const useTourStore = defineStore('tour', () => {
  const visible = ref(false);

  function hasSeenThisSession(): boolean {
    if (!import.meta.client) {
      return false;
    }
    return sessionStorage.getItem(SEEN_KEY) === '1';
  }

  function markSeen(): void {
    if (import.meta.client) {
      sessionStorage.setItem(SEEN_KEY, '1');
    }
  }

  function resetSeen(): void {
    if (import.meta.client) {
      sessionStorage.removeItem(SEEN_KEY);
    }
  }

  function open(): void {
    visible.value = true;
  }

  function close(): void {
    visible.value = false;
  }

  /** Open the tour unless it is already open or has been seen this session. */
  function autoStart(): void {
    if (!import.meta.client || visible.value || hasSeenThisSession()) {
      return;
    }
    open();
  }

  return { visible, open, close, autoStart, markSeen, resetSeen, hasSeenThisSession };
});
